import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollText, Plus, Loader2, ArrowUp, ArrowDown, ArrowLeftRight } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { ModuleGate } from "@/components/PlanNotIncluded";

type Item = { id: string; sku: string | null; name: string; unit: string; stock_by_branch: Record<string, number>; stock_total: number };
type Branch = { id: string; name: string };

export default function Movements() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const canMove = hasPerm("inventory.movement");
    const canTransfer = hasPerm("inventory.transfer");
    const [items, setItems] = useState<Item[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [msg, setMsg] = useState<string | null>(null);
    const [mode, setMode] = useState<"in" | "out" | "adjust" | "transfer">("in");
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ item_id: "", branch_id: "", to_branch_id: "", qty: 1, reason: "", adjust_direction: "increase" as "increase" | "decrease" });

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const [it, br] = await Promise.all([
                api.get("/inventory/items"),
                api.get("/branches").catch(() => ({ data: { items: [] } })),
            ]);
            setItems(it.data.items || []);
            setBranches(br.data.items || []);
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true); setErr(null); setMsg(null);
        try {
            if (mode === "transfer") {
                await api.post("/inventory/transfer", {
                    item_id: form.item_id, from_branch_id: form.branch_id,
                    to_branch_id: form.to_branch_id, qty: Number(form.qty), reason: form.reason.trim() || null,
                });
            } else {
                const payload: any = {
                    item_id: form.item_id, branch_id: form.branch_id,
                    movement_type: mode, qty: Number(form.qty), reason: form.reason.trim() || null,
                };
                if (mode === "adjust") payload.adjust_direction = form.adjust_direction;
                await api.post("/inventory/movements", payload);
            }
            setMsg(t("inventory.movements.msg.saved"));
            setForm({ item_id: "", branch_id: "", to_branch_id: "", qty: 1, reason: "", adjust_direction: "increase" });
            await load();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setSaving(false); }
    };

    const selectedItem = items.find((it) => it.id === form.item_id);

    return (
        <ModuleGate module="inventory">
            <div className="space-y-4" data-testid="inv-movements-page">
                <header className="flex items-center gap-3 flex-wrap">
                    <ScrollText size={22} style={{ color: "var(--primary)" }} />
                    <h1 className="text-xl font-semibold flex-1" style={{ color: "var(--text)" }}>{t("inventory.movements.title")}</h1>
                </header>
                {(!canMove && !canTransfer) && <div className="rounded-md p-3 text-sm" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="inv-movements-noperm">{t("common.no_permission")}</div>}

                {(canMove || canTransfer) && (
                    <div className="rounded-xl border p-5" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="inv-movements-form">
                        <div className="flex flex-wrap gap-2 mb-4">
                            {(["in", "out", "adjust", "transfer"] as const).map((m) => {
                                const enabled = m === "transfer" ? canTransfer : canMove;
                                if (!enabled) return null;
                                return (
                                    <button key={m} type="button" data-testid={`inv-mode-${m}`} onClick={() => setMode(m)}
                                        className="rounded-md px-3 py-2 text-xs font-medium inline-flex items-center gap-1"
                                        style={{ background: mode === m ? "var(--primary)" : "var(--input)", color: mode === m ? "var(--primary-fg)" : "var(--text)", minHeight: 40 }}>
                                        {m === "in" && <ArrowDown size={12} />}
                                        {m === "out" && <ArrowUp size={12} />}
                                        {m === "transfer" && <ArrowLeftRight size={12} />}
                                        {t(`inventory.movements.mode.${m}`)}
                                    </button>
                                );
                            })}
                        </div>
                        {err && <div className="rounded-md p-2 text-xs mb-3" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="inv-movements-err">{err}</div>}
                        {msg && <div className="rounded-md p-2 text-xs mb-3" style={{ background: "var(--success-bg)", color: "var(--success-fg)" }} data-testid="inv-movements-msg">{msg}</div>}
                        <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <label className="col-span-2 block text-xs" style={{ color: "var(--text-muted)" }}>
                                <span>{t("inventory.movements.item")} *</span>
                                <select required data-testid="inv-mv-item" value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })}
                                    className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }}>
                                    <option value="">—</option>
                                    {items.map((it) => <option key={it.id} value={it.id}>{it.sku ? `${it.sku} · ` : ""}{it.name}</option>)}
                                </select>
                            </label>
                            <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                <span>{mode === "transfer" ? t("inventory.movements.from_branch") : t("inventory.movements.branch")} *</span>
                                <select required data-testid="inv-mv-branch" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                                    className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }}>
                                    <option value="">—</option>
                                    {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </select>
                            </label>
                            {mode === "transfer" && (
                                <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                    <span>{t("inventory.movements.to_branch")} *</span>
                                    <select required data-testid="inv-mv-tobranch" value={form.to_branch_id} onChange={(e) => setForm({ ...form, to_branch_id: e.target.value })}
                                        className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }}>
                                        <option value="">—</option>
                                        {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </label>
                            )}
                            <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                <span>{t("inventory.movements.qty")} *</span>
                                <input required type="number" step="0.01" min={0.01} data-testid="inv-mv-qty" value={form.qty} onChange={(e) => setForm({ ...form, qty: parseFloat(e.target.value || "0") })}
                                    className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                            </label>
                            {mode === "adjust" && (
                                <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                    <span>{t("inventory.movements.adjust_direction")}</span>
                                    <select data-testid="inv-mv-adjustdir" value={form.adjust_direction} onChange={(e) => setForm({ ...form, adjust_direction: e.target.value as "increase" | "decrease" })}
                                        className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }}>
                                        <option value="increase">{t("inventory.movements.increase")}</option>
                                        <option value="decrease">{t("inventory.movements.decrease")}</option>
                                    </select>
                                </label>
                            )}
                            <label className="col-span-full block text-xs" style={{ color: "var(--text-muted)" }}>
                                <span>{t("inventory.movements.reason")}{mode === "adjust" ? " *" : ""}</span>
                                <input required={mode === "adjust"} data-testid="inv-mv-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                    className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                            </label>
                            {selectedItem && form.branch_id && (
                                <div className="col-span-full text-xs" style={{ color: "var(--text-muted)" }} data-testid="inv-mv-stockhint">
                                    {t("inventory.movements.current_stock")}: <span style={{ color: "var(--text)" }}>{selectedItem.stock_by_branch[form.branch_id] ?? 0} {selectedItem.unit}</span>
                                </div>
                            )}
                            <div className="col-span-full flex justify-end">
                                <button type="submit" disabled={saving} data-testid="inv-mv-save" className="rounded-md px-3 py-2 text-sm font-medium inline-flex items-center gap-2" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                                    {saving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} {t("inventory.movements.submit")}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <section className="rounded-xl border p-5" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="inv-stock-summary">
                    <h2 className="text-sm font-semibold mb-3" style={{ color: "var(--text)" }}>{t("inventory.movements.stock_by_branch")}</h2>
                    {loading && <div className="flex justify-center p-4"><Loader2 className="animate-spin" size={18} /></div>}
                    {!loading && (
                        <div className="overflow-x-auto rounded-md border" style={{ borderColor: "var(--border)" }}>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                        <th className="text-left px-3 py-2">{t("inventory.item.name")}</th>
                                        {branches.map((b) => <th key={b.id} className="text-right px-3 py-2">{b.name}</th>)}
                                        <th className="text-right px-3 py-2">{t("inventory.item.stock_total")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((it) => (
                                        <tr key={it.id} className="border-t" style={{ borderColor: "var(--border)" }}>
                                            <td className="px-3 py-2" style={{ color: "var(--text)" }}>{it.name}</td>
                                            {branches.map((b) => (
                                                <td key={b.id} className="px-3 py-2 text-right font-mono" style={{ color: "var(--text-muted)" }}>{it.stock_by_branch[b.id] ?? 0}</td>
                                            ))}
                                            <td className="px-3 py-2 text-right font-mono font-semibold" style={{ color: "var(--text)" }}>{it.stock_total}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        </ModuleGate>
    );
}
